import { useEffect, useState } from 'react';

/**
 * The announcement bar above the landing page.
 *
 * A strip that scrolls the benefits of Valdon Site Control across the top
 * of the page, so the first thing a visitor sees says what the product is
 * for. It is a marquee, so it moves on its own: it pauses while the
 * pointer is over it or while it holds the keyboard focus, it has a
 * button to stop it for good, and it does not move at all for people who
 * ask for reduced motion — there the list simply scrolls by hand.
 *
 * A moving bar is easy to get wrong: it becomes noise the moment a
 * visitor has seen it a few times. So it is kept quiet — a thin line of
 * muted type rather than a shout, a slow drift, and a control that only
 * shows itself when it is wanted. If the visitor stops it, that choice is
 * remembered, so the bar greets them the same quiet way next time.
 */

const BENEFITS = [
  'Site agents record daily reports, labour and photos on site',
  'Managers approve every record before the client sees it',
  'Clients follow verified progress, materials and costs as work happens',
  'Plant, deliveries and quality checks kept in one record',
  'Works on the phone on site and the laptop in the office',
];

const PAUSED_KEY = 'vsc360.announceStill';

export default function AnnouncementBar() {
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  // A visitor who stopped the bar before should not have it start moving
  // at them again.
  useEffect(() => {
    let saved = '';
    try { saved = localStorage.getItem(PAUSED_KEY) || ''; } catch { /* private mode */ }
    if (saved === '1') setPaused(true);
  }, []);

  function setStill(still) {
    setPaused(still);
    try {
      if (still) localStorage.setItem(PAUSED_KEY, '1');
      else localStorage.removeItem(PAUSED_KEY);
    } catch { /* private mode */ }
  }

  // The scroll is a CSS animation, so pausing is a matter of telling it to
  // hold still; the track keeps its place either way.
  const still = paused || reduced;

  return (
    <aside className="landing-announce" aria-label="What Valdon Site Control gives you">
      <span className="landing-announce-label" aria-hidden="true">What you get</span>

      <div
        className="landing-announce-viewport"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <div
          className={`landing-announce-track${still ? ' is-still' : ''}`}
          aria-live="off"
        >
          <ul className="landing-announce-group">
            {BENEFITS.map((b) => <li key={b}>{b}</li>)}
          </ul>
          {/* The second copy makes the loop seamless; it is hidden from
              assistive technology so the benefits are not read twice. */}
          <ul className="landing-announce-group" aria-hidden="true">
            {BENEFITS.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
      </div>

      {!reduced && (
        <button
          type="button"
          className="landing-announce-toggle"
          aria-pressed={paused}
          onClick={() => setStill(!paused)}
          title={paused ? 'Let the benefits scroll again' : 'Stop the benefits scrolling'}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            {paused ? 'play_arrow' : 'pause'}
          </span>
          <span className="landing-announce-toggle-text">{paused ? 'Play' : 'Pause'}</span>
        </button>
      )}
    </aside>
  );
}
